import { HardDrive, Inbox, User } from "lucide-react";
import type { ReactNode } from "react";
import { ICON, Pill, ProgressBar, StatusPill, type LadderState } from "./status.tsx";

/** White, thin-bordered card. The only surface allowed inside it is one NestedCard. */
export function Card({
  title,
  actions,
  children,
  className = "",
  label,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Names the card as a landmark region (for cards a page has several of). */
  label?: string;
}) {
  return (
    <section className={`card ${className}`.trim()} {...(label ? { "aria-label": label } : {})}>
      {(title || actions) && (
        <header className="card-head">
          {title && <h2>{title}</h2>}
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function NestedCard({ children }: { children: ReactNode }) {
  return <div className="nested">{children}</div>;
}

/** Large number with a caption; the 28-40 px step is reserved for these. */
export function Metric({
  value,
  caption,
  size = "xl",
}: {
  value: ReactNode;
  caption: ReactNode;
  size?: "xl" | "2xl";
}) {
  return (
    <div className="metric">
      <span className={`metric-value ${size}`}>{value}</span>
      <span className="metric-caption">{caption}</span>
    </div>
  );
}

export function SourceRow({
  name,
  detail,
  state,
  icon = <HardDrive {...ICON} />,
}: {
  name: string;
  detail?: ReactNode;
  state?: LadderState | "FAILED";
  icon?: ReactNode;
}) {
  return (
    <div className="row-item">
      <span className="row-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="row-main">
        <span className="row-title">{name}</span>
        {detail && <span className="row-detail">{detail}</span>}
      </span>
      {state && <StatusPill state={state} />}
    </div>
  );
}

/**
 * One member's sync summary. Without a scan (`lastScan`/`discovered` null) it says "Not scanned"
 * and shows no completion, never 100 %.
 */
export function PersonRow({
  name,
  lastScan,
  verified,
  discovered,
  pending,
  progress,
}: {
  name: string;
  lastScan: string | null;
  verified: string;
  discovered: string | null;
  pending: string | null;
  progress: number | null;
}) {
  const scanned = lastScan !== null && discovered !== null;
  return (
    <div className="row-item person">
      <span className="avatar" aria-hidden="true">
        {name.trim()[0]?.toUpperCase() ?? <User {...ICON} />}
      </span>
      <span className="row-main">
        <span className="row-title">{name}</span>
        <span className="row-detail">
          {scanned ? `Scanned ${lastScan} · ${verified} verified of ${discovered}` : "No scan yet"}
          {scanned && pending ? ` · ${pending} pending` : ""}
        </span>
        {scanned && (
          <ProgressBar value={progress} label={`${name}: verified of discovered`} tone="good" />
        )}
      </span>
      {!scanned && <Pill tone="wait">Not scanned</Pill>}
    </div>
  );
}

/** Rounded media preview in a fixed aspect-ratio box, so loading never shifts the layout. */
export function Thumb({
  src,
  alt,
  ratio = "4/3",
}: {
  src: string | null;
  alt: string;
  ratio?: "4/3" | "16/9" | "1/1";
}) {
  return (
    <div className="thumb" style={{ aspectRatio: ratio }}>
      {src ? (
        <img src={src} alt={alt} loading="lazy" decoding="async" />
      ) : (
        <span className="sr">{alt}</span>
      )}
    </div>
  );
}

/** What this is, plus the action that fills it, and optionally a second way out. */
export function EmptyState({
  title,
  body,
  action,
  secondary,
  icon = <Inbox size={24} strokeWidth={1.5} />,
}: {
  title: string;
  body?: ReactNode;
  action?: { label: string; onClick: () => void };
  /** A second way out, beside the first and quieter than it. */
  secondary?: { label: string; onClick: () => void };
  icon?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty-icon" aria-hidden="true">
        {icon}
      </span>
      <p className="empty-title">{title}</p>
      {body && <p className="muted">{body}</p>}
      {(action || secondary) && (
        <div className="empty-actions">
          {action && (
            <button type="button" className="button primary" onClick={action.onClick}>
              {action.label}
            </button>
          )}
          {secondary && (
            <button type="button" className="button" onClick={secondary.onClick}>
              {secondary.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
